/**
 * Accept Emergency — doctor reviews emergency details and accepts to start consultation.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doctorAPI, appointmentAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

const AcceptEmergency = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const appointmentId = searchParams.get('id') || '';

  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  const loadAppointment = useCallback(async () => {
    if (!appointmentId) {
      toast.error('No emergency ID provided.');
      navigate('/doctor/dashboard');
      return;
    }
    try {
      const res = await appointmentAPI.getDetail(appointmentId);
      const data = res.data?.data || res.data;
      if (!data) {
        toast.error('Emergency not found.');
        navigate('/doctor/dashboard');
        return;
      }
      if (data.status !== 'emergency_pending') {
        // Already claimed by another doctor — show a specific state instead of bouncing away
        setAppointment({ ...data, _already_claimed: true });
        setLoading(false);
        return;
      }
      setAppointment(data);
    } catch {
      toast.error('Unable to load emergency details.');
      navigate('/doctor/dashboard');
    } finally {
      setLoading(false);
    }
  }, [appointmentId, navigate]);

  useEffect(() => { loadAppointment(); }, [loadAppointment]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await doctorAPI.acceptEmergency({ appointment_id: appointmentId });
      const roomId = res.data?.data?.room_id;
      toast.success('Emergency accepted! Starting consultation...');
      // Navigate directly into the video consultation room
      navigate(`/consultation?room=${roomId}&appointment=${appointmentId}`);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : (err?.error || err?.message || 'Failed to accept emergency.'));
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    navigate('/doctor/dashboard');
  };

  if (loading) {
    return (
      <Layout title="Emergency Request" role="doctor" showBack>
        <div className="p-4 space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="skeleton h-20 rounded-2xl" />)}
        </div>
      </Layout>
    );
  }

  // Another doctor already accepted this emergency
  if (appointment?._already_claimed) {
    return (
      <Layout title="Emergency Request" role="doctor" showBack>
        <div className="p-4 pb-24 space-y-4">
          <div className="card text-center py-10 space-y-3">
            <div className="text-5xl">✅</div>
            <h2 className="text-lg font-bold text-gray-800">Already Handled</h2>
            <p className="text-sm text-gray-500">Another doctor has already accepted this emergency consultation.</p>
            <button onClick={() => navigate('/doctor/dashboard')} className="btn-primary mt-2">
              Back to Dashboard
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const severityColor = {
    critical: 'bg-red-100 text-red-800 border-red-300',
    high: 'bg-orange-100 text-orange-800 border-orange-300',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    low: 'bg-green-100 text-green-800 border-green-300',
  };
  const severity = appointment?.severity || 'high';

  return (
    <Layout title="Emergency Request" role="doctor" showBack>
      <div className="p-4 pb-24 space-y-4">
        {/* Alert header */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <span className="relative flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500"></span>
            </span>
            <h2 className="text-lg font-bold text-red-800">Emergency Consultation Request</h2>
          </div>
          <p className="text-sm text-red-600">A patient needs immediate medical attention.</p>
        </div>

        {/* Patient & severity details */}
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Patient</p>
              <p className="text-base font-semibold text-gray-900 mt-0.5">
                {appointment?.patient_name || 'Patient'}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-bold border ${severityColor[severity] || severityColor.high}`}>
              {severity.charAt(0).toUpperCase() + severity.slice(1)} Severity
            </span>
          </div>

          {appointment?.symptoms && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Reported Symptoms</p>
              <p className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3 leading-relaxed">
                {appointment.symptoms}
              </p>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-gray-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Requested {appointment?.appointment_date ? new Date(appointment.appointment_date).toLocaleString() : 'just now'}
          </div>
        </div>

        {/* Info box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-800">
          <p className="font-medium">What happens when you accept?</p>
          <ul className="mt-1 space-y-0.5 text-blue-600 text-xs list-disc list-inside">
            <li>A video consultation room is created instantly</li>
            <li>The patient is notified and redirected to join</li>
            <li>You will be taken directly into the consultation</li>
            <li>Emergency consultations bypass the payment requirement</li>
          </ul>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleAccept}
            disabled={accepting}
            className="w-full py-3.5 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
          >
            {accepting && <div className="spinner w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
            {accepting ? 'Starting consultation...' : 'Accept & Start Consultation'}
          </button>
          <button
            onClick={handleDecline}
            disabled={accepting}
            className="w-full py-3 rounded-xl font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 disabled:opacity-60 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default AcceptEmergency;
